/**
 * Cookie-SSO fetch wrapper (same model as CRM/tasks). All requests carry the
 * shared g1_access cookie (`credentials: "include"`); on 401 we refresh via the
 * shared-api JWT signer and, failing that, bounce to auth.g1.network.
 */
import { API_BASE, AUTH_URL, SHARED_API_URL } from "../config.ts";

export class ApiError extends Error {
	constructor(
		public status: number,
		message: string,
	) {
		super(message);
	}
}

let refreshing: Promise<boolean> | null = null;
async function tryRefresh(): Promise<boolean> {
	if (refreshing) return refreshing;
	refreshing = (async () => {
		try {
			const res = await fetch(`${SHARED_API_URL}/auth/refresh`, {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: "{}",
			});
			return res.ok;
		} catch {
			return false;
		} finally {
			setTimeout(() => {
				refreshing = null;
			}, 100);
		}
	})();
	return refreshing;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
	const doFetch = () => fetch(`${API_BASE}${path}`, { ...init, credentials: "include" });

	let res = await doFetch();
	if (res.status === 401) {
		const ok = await tryRefresh();
		if (ok) res = await doFetch();
		if (res.status === 401) {
			window.location.replace(`${AUTH_URL}/?return=${encodeURIComponent(window.location.href)}`);
			throw new ApiError(401, "unauthorized");
		}
	}
	if (!res.ok) {
		const body = await res.text().catch(() => "");
		throw new ApiError(res.status, body.slice(0, 300) || res.statusText);
	}
	if (res.status === 204) return undefined as T;
	return (await res.json()) as T;
}

export const api = {
	get: <T>(path: string) => request<T>(path),
	post: <T>(path: string, body?: unknown) =>
		request<T>(path, {
			method: "POST",
			headers: body === undefined ? undefined : { "content-type": "application/json" },
			body: body === undefined ? undefined : JSON.stringify(body),
		}),
	patch: <T>(path: string, body?: unknown) =>
		request<T>(path, {
			method: "PATCH",
			headers: body === undefined ? undefined : { "content-type": "application/json" },
			body: body === undefined ? undefined : JSON.stringify(body),
		}),
	postForm: <T>(path: string, form: FormData) => request<T>(path, { method: "POST", body: form }),
	del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

/** rbac-ui AuthProvider fetchUser — same-origin /api/me (session-only gate). */
export async function fetchMe<T = unknown>(): Promise<T> {
	return request<T>("/api/me");
}

export async function logout(): Promise<void> {
	try {
		await fetch(`${AUTH_URL}/auth/logout`, { method: "POST", credentials: "include" });
	} catch {
		// best-effort
	}
	window.location.replace(`${AUTH_URL}/?return=${encodeURIComponent(window.location.origin)}`);
}
